// Runtime switches set in Django admin (/djadmin → Platform settings).
//
// The backend returns only the frontend switches that were overridden in the
// DB (GET accounts/runtime-flags/). Anything not returned keeps its build-time
// VITE_* value, so a missing/failed fetch behaves exactly like before.
import { useEffect, useState } from "react";
import axios from "axios";
import { baseURL } from "../api/instance";

const STORAGE_KEY = "haire_runtime_flags_v1";
const MAX_AGE_MS = 5 * 60 * 1000;

let flags = null;
let inflight = null;
const listeners = new Set();

const readStored = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed.flags || {};
  } catch {
    return null;
  }
};

const store = (value) => {
  flags = value;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), flags: value }));
  } catch {
    // storage unavailable (private mode): memory copy is enough
  }
  listeners.forEach((fn) => fn());
};

/** Fetch once per session (cached 5 min); never throws. */
export const loadRuntimeFlags = () => {
  if (flags) return Promise.resolve(flags);
  const stored = readStored();
  if (stored) {
    store(stored);
    return Promise.resolve(flags);
  }
  if (!inflight) {
    inflight = axios
      .get(`${baseURL}accounts/runtime-flags/`, { timeout: 5000 })
      .then((res) => store(res?.data?.data?.flags || {}))
      .catch(() => store({}))
      .finally(() => {
        inflight = null;
      });
  }
  return inflight.then(() => flags);
};

/** Synchronous read: the DB override if loaded, else `fallback` (the VITE_* value). */
export const getRuntimeFlag = (key, fallback) => {
  const current = flags || readStored();
  if (current && Object.prototype.hasOwnProperty.call(current, key)) {
    return current[key];
  }
  return fallback;
};

/** Hook form; re-renders when the flags arrive. */
export const useRuntimeFlag = (key, fallback) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    loadRuntimeFlags();
    return () => listeners.delete(fn);
  }, []);
  return getRuntimeFlag(key, fallback);
};

// Test helper.
export const __resetRuntimeFlags = () => {
  flags = null;
  inflight = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
